import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { platformApi } from '@/services/platformApi';

interface MerchantStatus {
  hasPayoutMethod: boolean;
  hasBusinessInfo: boolean;
  hasProducts: boolean;
  profileCompleteness: number;
  payoutMethods: {
    hasStripeConnect: boolean;
    hasBankAccount: boolean;
    hasPayPal: boolean;
  };
  balance?: {
    available: number;
    pending: number;
    currency: string;
  };
  loading: boolean;
}

export const useMerchantStatus = () => {
  const { user } = useAuth();
  const [status, setStatus] = useState<MerchantStatus>({
    hasPayoutMethod: false,
    hasBusinessInfo: false,
    hasProducts: false,
    profileCompleteness: 0,
    payoutMethods: {
      hasStripeConnect: false,
      hasBankAccount: false,
      hasPayPal: false,
    },
    loading: true,
  });

  useEffect(() => {
    if (!user || !user.roles?.includes('MERCHANT')) {
      setStatus(prev => ({ ...prev, loading: false }));
      return;
    }

    checkMerchantStatus();
  }, [user]);

  const checkMerchantStatus = async () => {
    try {
      // Fetch all merchant data in parallel
      const [profile, stripeConnect, products, balance] = await Promise.all([
        platformApi.getMerchantProfile().catch(() => null),
        platformApi.merchant.getMerchantStripeConnectStatus().catch(() => null),
        platformApi.getProducts({ limit: 1 }).catch(() => ({ products: [] })),
        platformApi.getBalance().catch(() => null),
      ]);

      // IMPORTANT: Also check user.otherProfiles.merchant as fallback
      // The getMerchantProfile endpoint might return null even when merchant data exists
      const merchantDataFromUser = user?.otherProfiles?.merchant;
      
      // Check business info completeness - check both sources
      const hasBusinessInfo = !!(
        (profile?.businessName || merchantDataFromUser?.businessName) &&
        (profile?.businessType || merchantDataFromUser?.businessType) &&
        (profile?.address?.street || profile?.location || merchantDataFromUser?.isPublished || merchantDataFromUser?.completedOnboarding)
      );

      // Check payout methods
      const hasStripeConnect = !!(
        stripeConnect?.stripeConnectId && stripeConnect.status === 'connected'
      );
      const hasBankAccount = !!(profile?.bankAccount?.accountNumber);
      const hasPayPal = !!(profile?.paypalEmail);
      const hasPayoutMethod = hasStripeConnect || hasBankAccount || hasPayPal;

      // Check if has products
      const hasProducts = products?.products?.length > 0;

      // Calculate profile completeness
      const completionSteps = [
        hasBusinessInfo,
        hasProducts,
        hasPayoutMethod,
        profile?.description,
        profile?.logo,
      ];
      const profileCompleteness = Math.round(
        (completionSteps.filter(Boolean).length / completionSteps.length) * 100
      );

      setStatus({
        hasPayoutMethod,
        hasBusinessInfo,
        hasProducts,
        profileCompleteness,
        payoutMethods: {
          hasStripeConnect,
          hasBankAccount,
          hasPayPal,
        },
        balance: balance ? {
          available: balance.available || 0,
          pending: balance.pending || 0,
          currency: balance.currency || 'USD',
        } : undefined,
        loading: false,
      });
    } catch (error) {
      console.error('Error checking merchant status:', error);
      setStatus(prev => ({ ...prev, loading: false }));
    }
  };

  return status;
};
